type GetType<T, K extends keyof T> = NonNullable<T[K]> extends {
  value: infer V;
}
  ? undefined extends T[K]
    ? V | undefined
    : V
  : undefined;

type ProductWithValue = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: { value: any } | undefined;
};

export function getAvailabilityStatus<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'disponibilite'> {
  return attributes.disponibilite?.value;
}

export function getBrandName<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'metaMarque'> {
  return attributes.metaMarque?.value;
}

export function getCategory1<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'codeLibelleFamille'> {
  return attributes.codeLibelleFamille?.value;
}

export function getCategory2<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'codeLibelleSsfamille'> {
  return attributes.codeLibelleSsfamille?.value;
}

export function getCategory3<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'subuniverse'> {
  return attributes.subuniverse?.value;
}

export function getColor<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'couleur'> {
  return attributes.couleur?.value;
}

export function getCommercialOperationSticker<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'stickerOpeco'> {
  return attributes.stickerOpeco?.value;
}

export function getDiscountPercentage<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'decote'> {
  return attributes.decote?.value;
}

export function getDiscountPrice<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'prixBarre'> {
  return attributes.prixBarre?.value;
}

export function getFinalPrice<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'prix'> {
  return attributes.prix?.value;
}

export function getMainSticker<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'stickerPrincipal'> {
  return attributes.stickerPrincipal?.value;
}

export function getName<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'libelle'> {
  return attributes.libelle?.value;
}

export function getRealSku<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'SKUReelle'> {
  return attributes.SKUReelle?.value;
}

export function getSizeLabel<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'libelleTaille'> {
  return attributes.libelleTaille?.value;
}

export function getSizesList<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'listeTailles'> {
  return attributes.listeTailles?.value;
}

export function getSubSticker1<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'stickerSecondaire1'> {
  return attributes.stickerSecondaire1?.value;
}

export function getSubSticker2<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'stickerSecondaire2'> {
  return attributes.stickerSecondaire2?.value;
}

export function getVisuelBack<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'imgDosPivot'> {
  return attributes.imgDosPivot?.value;
}

export function getVisuelBackCurvy<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'imgDosCurvy'> {
  return attributes.imgDosCurvy?.value;
}

export function getVisuelDetail<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'imgDetailPivot'> {
  return attributes.imgDetailPivot?.value;
}

export function getVisuelDetailCurvy<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'imgDetailCurvy'> {
  return attributes.imgDetailCurvy?.value;
}

export function getVisuelFace<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'imgFacePivot'> {
  return attributes.imgFacePivot?.value;
}

export function getVisuelFaceCurvy<T extends ProductWithValue>(
  attributes: T
): GetType<T, 'imgFaceCurvy'> {
  return attributes.imgFaceCurvy?.value;
}

export function getLotQuantity<T extends ProductWithValue>(
  attribute: T
): number | undefined {
  const lotQuantity = parseFloat(attribute.metaLot?.value);

  return isNaN(lotQuantity) ? undefined : lotQuantity;
}

export function hasUniqPrice<T extends { prixUnique?: { value: string } }>(
  attributes: T
): boolean | undefined {
  return attributes.prixUnique?.value === '1';
}
